import * as React from 'react'

import {
  Button,
  Flex,
  Heading,
  Link,
  Separator,
  Text,
} from '@buffer-mono/popcorn'

import ConfirmRevokeAccessModal from './components/ConfirmRevokeAccessModal'
import { NewSettingsPageLayout } from '../shared/components/NewSettingsPageLayout'
import { SettingsRow } from '../shared/components/SettingsRow'
import { useConnectedApps } from '../shared/hooks/useConnectedApps'
import { useRevokeApp } from '../shared/hooks/useRevokeApp'

import styles from './AppsExtrasPage.module.css'

import type { JSX } from 'react'

const formatConnectedDate = (dateString: string): string =>
  new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })

const ConnectedApps = (): JSX.Element | null => {
  const [isOpen, setIsOpen] = React.useState(false)
  const [appId, setAppId] = React.useState('')
  const [appName, setAppName] = React.useState('')
  const [appConnectedDate, setAppConnectedDate] = React.useState<
    string | undefined
  >(undefined)

  const { connectedApps } = useConnectedApps()
  const { revokeAccessConnectedApp } = useRevokeApp()

  const handleOpenModal = (
    id: string,
    selectedAppName: string,
    connectedDate?: string,
  ): void => {
    setIsOpen(true)
    setAppId(id)
    setAppName(selectedAppName)
    setAppConnectedDate(connectedDate)
  }

  const handleRevokeAccess = (): void => {
    void revokeAccessConnectedApp({ clientId: appId })
    setIsOpen(false)
  }

  const handleCloseModal = (): void => {
    setIsOpen(false)
  }

  // MCP connections are managed on /settings/api, under Active Integrations.
  const nonMcpApps = connectedApps.filter((app) => app.category !== 'mcp')

  if (nonMcpApps.length === 0) {
    return null
  }

  return (
    <>
      <Separator className={styles.separator} />
      <Flex gap="xs" justify="between" fullWidth>
        <ConfirmRevokeAccessModal
          isOpen={isOpen}
          onRevokeAccess={handleRevokeAccess}
          close={handleCloseModal}
          appName={appName}
          connectedDate={appConnectedDate}
        />
        <Flex gap="xs" align="baseline" direction="column">
          <Heading as="h2" size="small" id="connected-apps-heading">
            Connected Apps
          </Heading>
          <Text id="connected-apps-desc" color="subtle">
            Connected Apps Get the most out of Buffer and share from your
            mobile, news reader, blog or anywhere!{' '}
            <Link href="https://buffer.com/extras" external>
              Get More Apps
            </Link>
          </Text>
        </Flex>
      </Flex>
      {nonMcpApps.map((app) => {
        const connectedDate = formatConnectedDate(app.createdAt)
        return (
          <SettingsRow key={app.clientId} id={`connected-apps-${app.clientId}`}>
            <SettingsRow.Header>
              <SettingsRow.Title as="h3" size="xs">
                {app.name}
              </SettingsRow.Title>
              <SettingsRow.Description>
                Connected {connectedDate}
                {app.description ? (
                  <>
                    <br />
                    {app.description}
                  </>
                ) : null}
              </SettingsRow.Description>
            </SettingsRow.Header>

            <SettingsRow.Control>
              {(ids) => (
                <Button
                  size="medium"
                  variant="secondary"
                  aria-label={`Revoke access for ${app.name} (${app.clientId})`}
                  aria-describedby={ids.descriptionId}
                  onClick={(): void =>
                    handleOpenModal(app.clientId, app.name, connectedDate)
                  }
                >
                  Revoke Access
                </Button>
              )}
            </SettingsRow.Control>
          </SettingsRow>
        )
      })}
    </>
  )
}

const Apps = (): JSX.Element => {
  return (
    <SettingsRow id="apps">
      <SettingsRow.Header>
        <SettingsRow.Title>Mobile Apps</SettingsRow.Title>
        <SettingsRow.Description>
          Share content and manage your Buffer account on the go with our mobile
          apps.
        </SettingsRow.Description>
      </SettingsRow.Header>
      <SettingsRow.Control>
        {(ids) => (
          <Flex align="center" gap="xs" wrap="wrap">
            <Button
              as="a"
              size="medium"
              variant="secondary"
              href="https://play.google.com/store/apps/details?id=org.buffer.android"
              target="_blank"
              rel="noopener noreferrer"
              aria-describedby={ids.descriptionId}
            >
              Android App
            </Button>
            <Button
              as="a"
              size="medium"
              variant="secondary"
              href="https://itunes.apple.com/app/apple-store/id490474324?pt=936146&ct=Web%20App%20Sidebar&mt=8"
              target="_blank"
              rel="noopener noreferrer"
              aria-describedby={ids.descriptionId}
            >
              iOS App
            </Button>
          </Flex>
        )}
      </SettingsRow.Control>
    </SettingsRow>
  )
}

const InstallExtension = (): JSX.Element => {
  return (
    <SettingsRow id="browser-extension">
      <SettingsRow.Header>
        <SettingsRow.Title>Browser Extension</SettingsRow.Title>
        <SettingsRow.Description>
          Our browser extension lets you share content as you browse the web.
        </SettingsRow.Description>
      </SettingsRow.Header>
      <SettingsRow.Control>
        {(ids) => (
          <Button
            as="a"
            size="medium"
            variant="secondary"
            href="https://buffer.com/extensions"
            target="_blank"
            rel="noopener noreferrer"
            aria-describedby={ids.descriptionId}
          >
            Install Extension
          </Button>
        )}
      </SettingsRow.Control>
    </SettingsRow>
  )
}

const AppsExtrasPage = (): JSX.Element => {
  return (
    <NewSettingsPageLayout title="Apps &amp; Extras" gap="xl">
      <InstallExtension />
      <Separator className={styles.separator} />
      <Apps />
      <ConnectedApps />
    </NewSettingsPageLayout>
  )
}

export default AppsExtrasPage
